import React, { useMemo } from "react";
import { Modal, Button, message } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import CWKOTPrinter from "../../../lib/printer/CWKotPrinter";
import "./restaurantStyle.css";

const ReprintKotDialog = ({ isOpen, onClose, groupedProducts = {} }) => {
  const groupedByCenter = useMemo(() => {
    const items = Array.isArray(groupedProducts.items) ? groupedProducts.items : [];
    const groups = {};

    items.forEach((item) => {
      if (item.order !== "Y") return; // Only fired items

      const pcId = item.productionCenterId || "unknown";
      const pcName = item.productionCenter || "Unknown";

      if (!groups[pcId]) {
        groups[pcId] = {
          name: pcName,
          items: [],
          productionCenterId: pcId,
        };
      }
      groups[pcId].items.push(item);
    });

    return groups;
  }, [groupedProducts]);

  const hasFiredItems = Object.keys(groupedByCenter).length > 0;

  // Helper: Create correct data format for CWKOTPrinter
  const createPrintData = (selectedItems) => ({
    ...groupedProducts,
    items: selectedItems, // ← Always flat array!
  });

  const handleReprintAll = (pcId, centerName, centerItems) => {
    message.loading({ content: `Reprinting ${centerName} KOT...`, key: "reprint" });

    const printData = createPrintData(centerItems);

    try {
      CWKOTPrinter(printData, false, false, true);
      message.success({ content: `${centerName} KOT reprinted`, key: "reprint", duration: 2 });
    } catch (err) {
      message.error({ content: "Print failed", key: "reprint" });
      console.error("KOT Print Error:", err);
    }
  };

  const handleReprintItem = (item) => {
    message.loading({ content: `Reprinting ${item.name}...`, key: "reprint-item" });

    const printData = createPrintData([item]);

    try {
      CWKOTPrinter(printData, false, false, true);
      message.success({ content: `${item.name} reprinted`, key: "reprint-item", duration: 2 });
    } catch (err) {
      message.error({ content: "Print failed", key: "reprint-item" });
      console.error("Single Item Print Error:", err);
    }
  };

  if (!isOpen) return null;

  const roundedButtonStyle = { borderRadius: "6px" };

  return (
    <Modal
      className="reprint-kot-modal"
      title={
        <div>
          <h2 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 600 }}>Reprint KOT</h2>
          <p style={{ margin: "4px 0 0", color: "#8c8c8c" }}>Select KOTs or specific items to reprint</p>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      centered
      footer={[
        <Button key="close" onClick={onClose} style={roundedButtonStyle}>
          Close
        </Button>,
      ]}
      //   style={{ top: 20 }}
      styles={{ body: { maxHeight: "65vh", overflow: "auto", padding: 0 } }}
    >
      {!hasFiredItems ? (
        <div style={{ padding: "60px 24px", textAlign: "center", color: "#999" }}>
          <p>No fired items available for reprint</p>
        </div>
      ) : (
        <div className="reprint-kot-content" style={{ padding: "24px" }}>
          {Object.entries(groupedByCenter).map(([pcId, { name, items }]) => (
            <div
              key={pcId}
              style={{
                border: "1px solid #d9d9d9",
                borderRadius: "8px",
                overflow: "hidden",
                marginBottom: "24px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <div
                className="reprint-kot-center-header"
                style={{
                  backgroundColor: "#fafafa",
                  padding: "12px 16px",
                  borderBottom: "1px solid #d9d9d9",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <h3 style={{ margin: 0, fontWeight: 600, color: "#262626" }}>{name}</h3>
                  <span style={{ fontSize: "0.8rem", color: "#888" }}>
                    {items.length} fired item{items.length > 1 ? "s" : ""}
                  </span>
                </div>
                <Button
                  type="primary"
                  size="middle"
                  icon={<PrinterOutlined />}
                  onClick={() => handleReprintAll(pcId, name, items)}
                  style={{ ...roundedButtonStyle, backgroundColor: "#3d4660", borderColor: "#3d4660" }}
                >
                  Reprint All
                </Button>
              </div>

              <div style={{ padding: "12px" }}>
                {items.map((item, idx) => (
                  <div
                    key={item.lineId || item.fbOrderLineId || idx}
                    className="reprint-kot-item-row"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px",
                      background: "#fff",
                      border: "1px solid #f0f0f0",
                      borderRadius: "6px",
                      marginBottom: "8px",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, marginBottom: "4px" }}>{item.name}</div>
                      <div style={{ fontSize: "0.8rem", color: "#666" }}>
                        Qty: {item.weight || item.firedQty || 1} {item.uom_name || ""}
                        {(item.shortDescription || item.notes) && <span style={{ color: "#1890ff", marginLeft: "8px" }}>• {item.shortDescription || item.notes}</span>}
                      </div>
                    </div>
                    <Button size="small" icon={<PrinterOutlined />} onClick={() => handleReprintItem(item)} style={roundedButtonStyle}>
                      Reprint
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default ReprintKotDialog;
